import { Mail, MessageSquare, Phone, Star } from "lucide-react";
import type { Vendor } from "@web/lib/queries-vendors";
import { NavBarAction, Row, StatusChip, Stack, Text } from "@web/ui";

/** One directory entry, with the three things somebody standing at an event
 * actually wants: who they are, whether their certificate is good, and a way to
 * reach them right now.
 *
 * The quick actions are icons, not text buttons. Three text buttons beside a row
 * body leave the name about 90px at 390px, which is the layout the responsive
 * standard exists to prevent; icons at 44px leave the name the rest of the row.
 */
export const COI_TONE = { on_file: "success", expired: "danger", none: "neutral" } as const;
export const COI_LABEL = { on_file: "Insured", expired: "Expired", none: "No COI" } as const;

export function coiTone(status: string) {
  return COI_TONE[status as keyof typeof COI_TONE] ?? "neutral";
}
export function coiLabel(status: string) {
  return COI_LABEL[status as keyof typeof COI_LABEL] ?? status;
}

export function VendorRow({ vendor, onOpen, showStar = false }: { vendor: Vendor; onOpen: () => void; showStar?: boolean }) {
  const subtitle = [vendor.contactName, vendor.phone].filter(Boolean).join(" · ") || vendor.email || "No contact details yet";
  return (
    <Row
      wrap
      icon={showStar ? <Star aria-label="Preferred here" size={18} /> : undefined}
      title={vendor.name}
      subtitle={subtitle}
      trailing={
        <Stack gap={1} align="end">
          <StatusChip tone={coiTone(vendor.coiStatus)}>{coiLabel(vendor.coiStatus)}</StatusChip>
          {vendor.coiExpiresOn && vendor.coiStatus !== "none" && (
            <Text as="span" variant="caption" tone="tertiary">to {vendor.coiExpiresOn}</Text>
          )}
        </Stack>
      }
      onClick={onOpen}
      actions={
        <>
          <NavBarAction label={`Call ${vendor.name}`} icon={<Phone aria-hidden size={20} />}
            disabled={!vendor.phone} disabledReason={vendor.phone ? null : "No phone number"}
            onClick={() => vendor.phone && window.location.assign(`tel:${vendor.phone}`)} />
          <NavBarAction label={`Text ${vendor.name}`} icon={<MessageSquare aria-hidden size={20} />}
            disabled={!vendor.phone} disabledReason={vendor.phone ? null : "No phone number"}
            onClick={() => vendor.phone && window.location.assign(`sms:${vendor.phone}`)} />
          <NavBarAction label={`Email ${vendor.name}`} icon={<Mail aria-hidden size={20} />}
            disabled={!vendor.email} disabledReason={vendor.email ? null : "No email address"}
            onClick={() => vendor.email && window.location.assign(`mailto:${vendor.email}`)} />
        </>
      }
    />
  );
}
